import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@shared/lib/utils';

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-center gap-2 overflow-hidden rounded-md border py-4 pl-4 pr-6 shadow-lg transition-all',
    '[&_[data-toast-close]]:text-base-foreground [&_[data-toast-close]]:hover:text-base-foreground',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-base-border bg-background text-base-foreground',
        success: 'border-base-border bg-background text-base-foreground',
        info: 'border-base-border bg-background text-base-foreground',
        error: [
          'border-[#DC2626] bg-[#DC2626] text-[var(--destructive-button-foreground,#FEF2F2)]',
          '[&_[data-toast-close]]:text-red-300 [&_[data-toast-close]]:hover:text-[var(--destructive-button-foreground,#FEF2F2)]',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 sm:top-4 sm:right-4 sm:max-w-[388px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    className={cn(
      toastVariants({ variant }),
      'animate-toast-in data-[state=closed]:animate-toast-out data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
      className
    )}
    {...props}
  />
));
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('flex-1 text-sm font-semibold leading-5', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    data-toast-close=""
    className={cn(
      'absolute right-1 top-1 flex size-6 shrink-0 items-center justify-center rounded-lg p-1 opacity-0 transition-opacity duration-fast ease-standard',
      'group-hover:opacity-50 hover:!opacity-100',
      'focus:opacity-100 focus:outline-none focus-visible:shadow-[0_0_0_3px_rgba(163,163,163,0.5)]',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" strokeWidth={1.33} aria-hidden />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

export {
  ToastProvider as ToastPrimitivesProvider,
  ToastViewport,
  Toast,
  ToastDescription,
  ToastClose,
  toastVariants,
};
